import type { CatalogProduct } from '../ProductPicker';

/** Suggestions at or above this score are preselected; the user can still change them. */
export const CONFIDENT_SCORE = 0.75;
const MIN_SCORE = 0.35;

export interface NameEntry {
  name: string;
  /** Every workspace row with this name, most reviewed first. */
  products: CatalogProduct[];
  tokens: string[];
  grams: Map<string, number>;
  gramCount: number;
}

export interface Suggestion {
  entry: NameEntry;
  score: number;
}

/** Lowercase, strip accents and punctuation, collapse whitespace. */
export const normaliseName = (name: string) =>
  name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const bigrams = (text: string) => {
  const padded = ` ${text} `;
  const grams = new Map<string, number>();
  for (let i = 0; i < padded.length - 1; i += 1) {
    const gram = padded.slice(i, i + 2);
    grams.set(gram, (grams.get(gram) ?? 0) + 1);
  }
  return { grams, gramCount: padded.length - 1 };
};

const toEntry = (name: string, products: CatalogProduct[]): NameEntry => {
  const norm = normaliseName(name);
  return {
    name,
    products: [...products].sort((a, b) => b.reviews - a.reviews),
    tokens: norm.split(' ').filter(Boolean),
    ...bigrams(norm),
  };
};

/** One entry per distinct product name, with precomputed bigrams. */
export const buildNameIndex = (catalog: CatalogProduct[]): NameEntry[] => {
  const byName = new Map<string, CatalogProduct[]>();
  catalog.forEach((p) =>
    byName.set(p.name, [...(byName.get(p.name) ?? []), p]),
  );
  return Array.from(byName, ([name, products]) => toEntry(name, products));
};

/**
 * Blend of character-bigram similarity (catches typos and spacing) and the
 * share of pasted words found at the start of a catalog word (catches
 * abbreviations and extra or missing words).
 */
const similarity = (query: NameEntry, candidate: NameEntry) => {
  let shared = 0;
  query.grams.forEach((count, gram) => {
    shared += Math.min(count, candidate.grams.get(gram) ?? 0);
  });
  const dice = (2 * shared) / (query.gramCount + candidate.gramCount);
  const found = query.tokens.filter((t) =>
    candidate.tokens.some((c) => c.startsWith(t) || t.startsWith(c)),
  );
  const wordRecall = query.tokens.length
    ? found.length / query.tokens.length
    : 0;
  return 0.6 * dice + 0.4 * wordRecall;
};

/** How many suggestions each name gets. */
export const SUGGESTION_LIMIT = 10;

/** The closest catalog names to a pasted name, best first; ties put the exact name, then the most reviewed, first. */
export const suggestNames = (
  pasted: string,
  index: NameEntry[],
  limit = SUGGESTION_LIMIT,
): Suggestion[] => {
  const query = toEntry(pasted, []);
  const exact = pasted.trim();
  const reviews = (e: NameEntry) =>
    e.products.reduce((sum, p) => sum + p.reviews, 0);
  return index
    .map((entry) => ({ entry, score: similarity(query, entry) }))
    .filter((s) => s.score >= MIN_SCORE)
    .sort(
      (a, b) =>
        b.score - a.score ||
        Number(b.entry.name === exact) - Number(a.entry.name === exact) ||
        reviews(b.entry) - reviews(a.entry),
    )
    .slice(0, limit);
};

/**
 * The suggestion safe to pick without a person choosing: confident, and not
 * tied with another name (e.g. `Nutella` and `NUTELLA`) unless it is the exact
 * name pasted. Expects `suggestNames` order and at least two suggestions.
 */
export const confidentPick = (
  pasted: string,
  suggestions: Suggestion[],
): Suggestion | null => {
  const [top, next] = suggestions;
  if (!top || top.score < CONFIDENT_SCORE) return null;
  if (next?.score === top.score && top.entry.name !== pasted.trim())
    return null;
  return top;
};

/** How close one catalog name is to a pasted name, on the same 0–1 scale as `suggestNames`. */
export const scoreName = (pasted: string, entry: NameEntry) =>
  similarity(toEntry(pasted, []), entry);

/**
 * Splits a catalog name into parts, marking the words that match a word of the
 * query (by prefix, as the scoring does), so matches can be highlighted.
 */
export const highlightParts = (
  name: string,
  query: string,
): { text: string; match: boolean }[] => {
  const tokens = normaliseName(query).split(' ').filter(Boolean);
  return name
    .split(/(\s+)/)
    .filter(Boolean)
    .map((text) => {
      const word = normaliseName(text);
      return {
        text,
        match:
          !!word &&
          tokens.some((t) => word.startsWith(t) || t.startsWith(word)),
      };
    });
};
